import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Calendar, Play, Printer, AlertCircle, CheckCircle2, Download, ChevronRight, FileText
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { generateAPSummaryPDF } from '../../utils/pdfExport';
import ReportWorkflowTrail from './ReportWorkflowTrail';
import ReportPeriodSelector, { getMonthDateRange, getYearDateRange } from './ReportPeriodSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function APSummaryReport({ suppliers = [], fmt, viewingStudentId, viewingStudentName, onNavigate, onOpenSource }) {
    const now = new Date();
    const initialMonth = now.getMonth() + 1;
    const initialYear = now.getFullYear();
    const initialRange = getMonthDateRange(initialYear, initialMonth);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [data, setData] = useState(null);
    const [viewType, setViewType] = useState('detailed');
    const [transactionType, setTransactionType] = useState('both');
    const [dateType, setDateType] = useState('transaction');
    const [periodMode, setPeriodMode] = useState('monthly');
    const [periodMonth, setPeriodMonth] = useState(initialMonth);
    const [periodYear, setPeriodYear] = useState(initialYear);
    const [filters, setFilters] = useState({
        date_from: initialRange.dateFrom,
        date_to: initialRange.dateTo,
        supplier: '',
        status: 'posted'
    });

    const applyPeriodMode = (mode) => {
        setPeriodMode(mode);
        setFilters(prev => {
            if (mode === 'monthly') {
                const range = getMonthDateRange(periodYear, periodMonth);
                return { ...prev, date_from: range.dateFrom, date_to: range.dateTo };
            }
            if (mode === 'annual') {
                const range = getYearDateRange(periodYear);
                return { ...prev, date_from: range.dateFrom, date_to: range.dateTo };
            }
            return {
                ...prev,
                date_from: prev.date_from || initialRange.dateFrom,
                date_to: prev.date_to || initialRange.dateTo,
            };
        });
    };

    const handlePeriodMonthChange = (month) => {
        setPeriodMode('monthly');
        setPeriodMonth(month);
        const range = getMonthDateRange(periodYear, month);
        setFilters(prev => ({ ...prev, date_from: range.dateFrom, date_to: range.dateTo }));
    };

    const handlePeriodYearChange = (year) => {
        setPeriodYear(year);
        const range = periodMode === 'annual'
            ? getYearDateRange(year)
            : getMonthDateRange(year, periodMonth);
        setFilters(prev => ({ ...prev, date_from: range.dateFrom, date_to: range.dateTo }));
    };

    const handleCustomDateChange = (field, value) => {
        setPeriodMode('custom');
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const openApTrail = (tab, label) => {
        onNavigate?.(tab);
        toast.info(`Opening ${label}. Use supplier, voucher/payment number, and posting status to trace this AP Summary amount back to source work.`);
    };

    const openSummarySource = (txn) => {
        const isPayment = String(txn.type || '').toLowerCase().startsWith('payment');
        const recordType = txn.record_type || (isPayment ? 'AP Payment' : 'AP Voucher');
        const sourceType = txn.source_type || (isPayment ? 'ap_payment' : 'ap_voucher');
        const workspaceTab = txn.workspace_tab || (isPayment ? 'appayments' : 'apvouchers');
        onOpenSource?.({
            source_type: sourceType,
            workspace_tab: workspaceTab,
            record_id: txn.record_id || '',
            record_number: txn.doc_number || '',
            doc_number: txn.doc_number || '',
            record_type: recordType,
            source_location: 'AP Summary detailed row',
            source_impact: 'Posted AP source rows affect AP Summary, GL, Trial Balance, and related AP reports.',
            source_next_action: 'Review the source status, posting date, supplier, amount, WTAX/payment evidence, and GL entries before correction.',
        });
    };
    
    const handleExportPDF = useCallback(() => {
        if (!data) return;
        setExporting(true);
        try {
            const result = generateAPSummaryPDF(data, filters, fmt);
            if (result.success) {
                console.log(`[AP Summary] PDF exported: ${result.filename} (${(result.blobSize / 1024).toFixed(2)} KB)`);
                toast.success(`PDF exported: ${result.filename} (${(result.blobSize / 1024).toFixed(2)} KB). Export only; AP source records and GL/Trial Balance balances were not changed.`);
            } else {
                console.error(`[AP Summary] PDF export failed:`, result.error);
                toast.error(`Failed to export PDF: ${result.error}`);
            }
        } catch (error) {
            console.error(`[AP Summary] Unexpected error:`, error);
            toast.error('Failed to export PDF: ' + error.message);
        } finally {
            setExporting(false);
        }
    }, [data, filters, fmt]);

    const handleGenerate = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            params.append('view', viewType);
            params.append('type', transactionType);
            params.append('date_type', dateType);
            params.append('date_from', filters.date_from);
            params.append('date_to', filters.date_to);
            params.append('status', filters.status);
            if (filters.supplier && filters.supplier !== 'all') {
                params.append('supplier', filters.supplier);
            }
            if (viewingStudentId) {
                params.append('student_id', viewingStudentId);
            }

            const response = await axios.get(`${API}/reports/ap-summary?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setData(response.data);
            toast.success('AP Summary generated on this report tab. It summarizes AP source records for the selected filters; no vouchers, payments, GL, or Trial Balance records were changed.');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to generate AP Summary');
        } finally {
            setLoading(false);
        }
    }, [viewType, transactionType, dateType, filters, viewingStudentId]);

    return (
        <div className="space-y-6" data-testid="ap-summary-tab">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-slate-900 font-['Manrope']">AP Summary</h2>
                    <p className="text-sm text-slate-500">
                        {viewingStudentName ? `Accounts Payable Summary for ${viewingStudentName}` : 'Accounts Payable Summary Report'}
                    </p>
                </div>
            </div>
            {viewingStudentName && (
                <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900" data-testid="ap-summary-student-scope">
                    Student review scope is active. This report uses AP vouchers and payments from {viewingStudentName}'s workspace only.
                </div>
            )}
            <ReportWorkflowTrail
                title="AP Summary trail"
                testId="ap-summary-workflow-trail"
                scope={viewingStudentName ? `Reviewing ${viewingStudentName}` : 'Your workspace'}
                items={[
                    {
                        label: 'Source records',
                        value: 'Uses AP Vouchers and AP Payments that match the selected date, supplier, status, and transaction filters.'
                    },
                    {
                        label: 'Where to verify',
                        value: 'Open AP Vouchers or AP Payments to inspect the original supplier document and posting state.'
                    },
                    {
                        label: 'GL / Trial Balance',
                        value: filters.status === 'posted'
                            ? 'Posted AP activity affects GL and Trial Balance.'
                            : 'Only posted rows affect GL and Trial Balance; drafts and cancelled rows are review evidence only.'
                    },
                    {
                        label: 'Next safe action',
                        value: 'Preview the report, compare AP outstanding to reconciliation, then correct the source voucher or payment if needed.'
                    }
                ]}
            />

            {/* Filters Panel */}
            <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <Label className="text-sm font-medium">View Type</Label>
                        <div className="flex gap-2 mt-1">
                            <Button
                                variant={viewType === 'detailed' ? 'default' : 'outline'}
                                onClick={() => setViewType('detailed')}
                                className={viewType === 'detailed' ? 'bg-[#2F8FCB]' : ''}
                                size="sm"
                            >
                                Detailed
                            </Button>
                            <Button
                                variant={viewType === 'summary' ? 'default' : 'outline'}
                                onClick={() => setViewType('summary')}
                                className={viewType === 'summary' ? 'bg-[#2F8FCB]' : ''}
                                size="sm"
                            >
                                Summary
                            </Button>
                        </div>
                    </div>
                    <div>
                        <Label className="text-sm">Transaction Type</Label>
                        <Select value={transactionType} onValueChange={setTransactionType}>
                            <SelectTrigger className="mt-1">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="both">Vouchers & Payments</SelectItem>
                                <SelectItem value="vouchers">Vouchers Only</SelectItem>
                                <SelectItem value="payments">Payments Only</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-sm">Date Type</Label>
                        <div className="flex gap-2 mt-1">
                            <Button
                                variant={dateType === 'transaction' ? 'default' : 'outline'}
                                onClick={() => setDateType('transaction')}
                                className={dateType === 'transaction' ? 'bg-[#2F8FCB]' : ''}
                                size="sm"
                            >
                                Transaction
                            </Button>
                            <Button
                                variant={dateType === 'posting' ? 'default' : 'outline'}
                                onClick={() => setDateType('posting')}
                                className={dateType === 'posting' ? 'bg-[#2F8FCB]' : ''}
                                size="sm"
                            >
                                Posting
                            </Button>
                        </div>
                    </div>
                    <div>
                        <Label className="text-sm">Status</Label>
                        <Select value={filters.status} onValueChange={(v) => setFilters(prev => ({ ...prev, status: v }))}>
                            <SelectTrigger className="mt-1">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All</SelectItem>
                                <SelectItem value="unposted">Unposted</SelectItem>
                                <SelectItem value="posted">Posted</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <ReportPeriodSelector
                    mode={periodMode}
                    onModeChange={applyPeriodMode}
                    month={periodMonth}
                    year={periodYear}
                    onMonthChange={handlePeriodMonthChange}
                    onYearChange={handlePeriodYearChange}
                    dateFrom={filters.date_from}
                    dateTo={filters.date_to}
                    onDateFromChange={(value) => handleCustomDateChange('date_from', value)}
                    onDateToChange={(value) => handleCustomDateChange('date_to', value)}
                    effectText="AP vouchers and payments inside this coverage appear here; only posted rows affect GL and Trial Balance."
                    testId="ap-summary-period"
                />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <Label className="text-sm">Supplier</Label>
                        <Select 
                            value={filters.supplier || 'all'} 
                            onValueChange={(v) => setFilters(prev => ({ ...prev, supplier: v === 'all' ? '' : v }))}
                        >
                            <SelectTrigger className="mt-1">
                                <SelectValue placeholder="All suppliers" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Suppliers</SelectItem>
                                {suppliers.map(s => (
                                    <SelectItem key={s.supplier_id} value={s.supplier_id}>
                                        {s.supplier_id} - {s.supplier_name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="flex gap-3 pt-2">
                    <Button
                        onClick={handleGenerate}
                        disabled={loading}
                        className="bg-[#2F8FCB] hover:bg-[#2577a8]"
                        data-testid="ap-summary-btn"
                    >
                        {loading ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                Generating...
                            </>
                        ) : (
                            <>
                                <Play className="w-4 h-4 mr-2" /> Preview
                            </>
                        )}
                    </Button>
                    <Button variant="outline" onClick={() => window.print()} disabled={!data}>
                        <Printer className="w-4 h-4 mr-2" /> Print
                    </Button>
                    <Button 
                        variant="outline" 
                        onClick={handleExportPDF} 
                        disabled={!data || exporting}
                        data-testid="export-ap-summary-pdf-btn"
                    >
                        <Download className="w-4 h-4 mr-2" /> {exporting ? 'Exporting...' : 'Export PDF'}
                    </Button>
                </div>
            </div>

            {/* Results */}
            {!data && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-12 text-center">
                    <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-600 mb-2">Click "Preview" to generate AP Summary</h3>
                    <p className="text-sm text-slate-400">View outstanding payables and payment history</p>
                </div>
            )}

            {data && (
                <div className="space-y-4">
                    {/* Report Header */}
                    <div className="bg-[#0F2A44] text-white rounded-lg p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold">
                                    AP Summary - {viewType === 'detailed' ? 'Detailed' : 'By Supplier'}
                                </h3>
                                <p className="text-white/70 text-sm">
                                    {data.date_from} to {data.date_to} - {data.status_filter === 'all' ? 'All Status' : data.status_filter}
                                </p>
                            </div>
                            <div className="text-right">
                                <p className="text-xs text-white/50">Generated</p>
                                <p className="text-sm">{new Date(data.generated_at).toLocaleString()}</p>
                            </div>
                        </div>
                    </div>

                    <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-4" data-testid="ap-summary-where-it-appears">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-4 flex-1">
                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">What changed</p>
                                    <p className="mt-1 text-sm text-slate-700">The summary was generated from AP vouchers and payments for the selected filters. No source record was edited.</p>
                                </div>
                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Where to find it</p>
                                    <p className="mt-1 text-sm text-slate-700">Vouchers stay in AP Vouchers, payments stay in AP Payments, outstanding balances reconcile to AP Aging, and WTAX details appear in AP WTAX.</p>
                                </div>
                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Report effect</p>
                                    <p className="mt-1 text-sm text-slate-700">Only posted AP rows affect GL and Trial Balance. Draft, submitted, returned, or cancelled rows are source evidence only.</p>
                                </div>
                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Next safe action</p>
                                    <p className="mt-1 text-sm text-slate-700">Open the source voucher or payment before creating corrections, reversals, or additional payment evidence.</p>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2 shrink-0">
                                <Button type="button" variant="outline" size="sm" onClick={() => openApTrail('apvouchers', 'AP Vouchers')} data-testid="ap-summary-open-vouchers">
                                    AP Vouchers <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                                <Button type="button" variant="outline" size="sm" onClick={() => openApTrail('appayments', 'AP Payments')} data-testid="ap-summary-open-payments">
                                    Payments <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                                <Button type="button" variant="outline" size="sm" onClick={() => openApTrail('apaging', 'AP Aging')} data-testid="ap-summary-open-aging">
                                    AP Aging <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                                <Button type="button" variant="outline" size="sm" onClick={() => openApTrail('apwithholding', 'AP WTAX')} data-testid="ap-summary-open-wtax">
                                    AP WTAX <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Totals */}
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
                        <div className="bg-white rounded-xl border border-blue-200 p-4">
                            <p className="text-sm text-slate-500">Gross Vouchered</p>
                            <p className="text-2xl font-bold text-blue-600">{fmt(data.totals.total_gross_vouchered ?? data.totals.total_vouchered)}</p>
                            <p className="text-xs text-slate-400">{data.totals.voucher_count} vouchers</p>
                        </div>
                        <div className="bg-white rounded-xl border border-rose-200 p-4">
                            <p className="text-sm text-slate-500">WTAX Withheld</p>
                            <p className="text-2xl font-bold text-rose-600">{fmt(data.totals.total_withholding || 0)}</p>
                            <p className="text-xs text-slate-400">Expanded withholding</p>
                        </div>
                        <div className="bg-white rounded-xl border border-indigo-200 p-4">
                            <p className="text-sm text-slate-500">Net Payable</p>
                            <p className="text-2xl font-bold text-indigo-600">{fmt(data.totals.total_net_payable ?? data.totals.total_vouchered)}</p>
                            <p className="text-xs text-slate-400">AP control basis</p>
                        </div>
                        <div className="bg-white rounded-xl border border-emerald-200 p-4">
                            <p className="text-sm text-slate-500">Total Paid</p>
                            <p className="text-2xl font-bold text-emerald-600">{fmt(data.totals.total_paid)}</p>
                            <p className="text-xs text-slate-400">{data.totals.payment_count} payments</p>
                        </div>
                        <div className="bg-white rounded-xl border border-amber-200 p-4">
                            <p className="text-sm text-slate-500">Outstanding</p>
                            <p className="text-2xl font-bold text-amber-600">{fmt(data.totals.outstanding)}</p>
                        </div>
                        {viewType === 'summary' && (
                            <div className="bg-white rounded-xl border border-purple-200 p-4">
                                <p className="text-sm text-slate-500">Suppliers</p>
                                <p className="text-2xl font-bold text-purple-600">{data.totals.supplier_count}</p>
                            </div>
                        )}
                    </div>

                    {/* GL Reconciliation Panel */}
                    {data.gl_reconciliation && (
                        <div className={`rounded-lg border-2 p-4 ${
                            data.gl_reconciliation.is_reconciled 
                                ? 'bg-emerald-50 border-emerald-300' 
                                : 'bg-red-50 border-red-300'
                        }`}>
                            <div className="flex items-start gap-4">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                    data.gl_reconciliation.is_reconciled 
                                        ? 'bg-emerald-200 text-emerald-700' 
                                        : 'bg-red-200 text-red-700'
                                }`}>
                                    {data.gl_reconciliation.is_reconciled ? (
                                        <CheckCircle2 className="w-6 h-6" />
                                    ) : (
                                        <AlertCircle className="w-6 h-6" />
                                    )}
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                        <h4 className={`font-semibold ${
                                            data.gl_reconciliation.is_reconciled 
                                                ? 'text-emerald-800' 
                                                : 'text-red-800'
                                        }`}>
                                            GL Reconciliation: {data.gl_reconciliation.is_reconciled ? 'MATCHED' : 'MISMATCH'}
                                        </h4>
                                        <span className={`text-xs px-2 py-1 rounded ${
                                            data.gl_reconciliation.is_reconciled 
                                                ? 'bg-emerald-200 text-emerald-800' 
                                                : 'bg-red-200 text-red-800'
                                        }`}>
                                            {data.gl_reconciliation.gl_entries_count} GL entries checked
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-3 gap-4 mt-3">
                                        <div>
                                            <p className="text-xs text-slate-500">AP Outstanding (Report)</p>
                                            <p className="font-semibold text-lg">{fmt(data.gl_reconciliation.ap_outstanding)}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-slate-500">GL AP Control Balance</p>
                                            <p className="font-semibold text-lg">{fmt(data.gl_reconciliation.gl_ap_balance)}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-slate-500">Variance</p>
                                            <p className={`font-semibold text-lg ${
                                                data.gl_reconciliation.variance === 0 
                                                    ? 'text-emerald-600' 
                                                    : 'text-red-600'
                                            }`}>
                                                {fmt(data.gl_reconciliation.variance)}
                                            </p>
                                        </div>
                                    </div>
                                    {data.reconciliation_warning && (
                                        <p className="mt-3 text-sm text-red-700 bg-red-100 px-3 py-2 rounded">
                                            ⚠️ {data.reconciliation_warning}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Detailed View */}
                    {viewType === 'detailed' && data.transactions && (
                        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                                    <tr>
                                        <th className="text-left px-4 py-3">Supplier</th>
                                        <th className="text-left px-4 py-3">Doc #</th>
                                        <th className="text-left px-4 py-3">Date</th>
                                        <th className="text-center px-4 py-3">Type</th>
                                        <th className="text-right px-4 py-3">Gross</th>
                                        <th className="text-right px-4 py-3">WTAX</th>
                                        <th className="text-right px-4 py-3">Net Amount</th>
                                        <th className="text-center px-4 py-3">Status</th>
                                        <th className="text-left px-4 py-3">Reference</th>
                                        <th className="text-center px-4 py-3">Source</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {data.transactions.length === 0 ? (
                                        <tr>
                                            <td colSpan="10" className="px-4 py-8 text-center text-slate-400">
                                                No transactions found
                                            </td>
                                        </tr>
                                    ) : (
                                        data.transactions.map((txn, idx) => (
                                            <tr key={idx} className="hover:bg-slate-50">
                                                <td className="px-4 py-3">
                                                    <p className="font-medium">{txn.supplier_name}</p>
                                                    <p className="text-xs text-slate-400">{txn.supplier_id}</p>
                                                </td>
                                                <td className="px-4 py-3 font-mono text-sm">{txn.doc_number}</td>
                                                <td className="px-4 py-3">{txn.date}</td>
                                                <td className="px-4 py-3 text-center">
                                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                        txn.type === 'Voucher' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'
                                                    }`}>
                                                        {txn.type}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-right font-mono">
                                                    {txn.type === 'Voucher' ? fmt(txn.gross_amount ?? txn.amount) : '-'}
                                                </td>
                                                <td className="px-4 py-3 text-right font-mono text-rose-600">
                                                    {txn.type === 'Voucher' && txn.withholding_amount ? fmt(txn.withholding_amount) : '-'}
                                                </td>
                                                <td className={`px-4 py-3 text-right font-mono ${txn.amount < 0 ? 'text-emerald-600' : ''}`}>
                                                    {fmt(txn.amount)}
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                        txn.status === 'posted' ? 'bg-emerald-100 text-emerald-700' :
                                                        txn.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                                        'bg-amber-100 text-amber-700'
                                                    }`}>
                                                        {txn.status}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-slate-500">{txn.reference || txn.bank || '-'}</td>
                                                <td className="px-4 py-3 text-center">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => openSummarySource(txn)}
                                                        data-testid={`ap-summary-open-source-${txn.doc_number || idx}`}
                                                        aria-label={`Open ${txn.record_type || txn.type || 'AP source'} ${txn.doc_number || ''} source evidence`}
                                                        title={`Open ${txn.record_type || txn.type || 'AP source'} ${txn.doc_number || ''} source evidence`}
                                                    >
                                                        <FileText className="w-4 h-4 mr-1" /> Open Source
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Summary View */}
                    {viewType === 'summary' && data.transactions && (
                        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                                    <tr>
                                        <th className="text-left px-4 py-3">Supplier</th>
                                        <th className="text-center px-4 py-3">Vouchers</th>
                                        <th className="text-center px-4 py-3">Payments</th>
                                        <th className="text-right px-4 py-3">Gross Vouchered</th>
                                        <th className="text-right px-4 py-3">WTAX</th>
                                        <th className="text-right px-4 py-3">Net Vouchered</th>
                                        <th className="text-right px-4 py-3">Total Paid</th>
                                        <th className="text-right px-4 py-3">Outstanding</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {data.transactions.length === 0 ? (
                                        <tr>
                                            <td colSpan="8" className="px-4 py-8 text-center text-slate-400">
                                                No data found
                                            </td>
                                        </tr>
                                    ) : (
                                        data.transactions.map((row, idx) => (
                                            <tr key={idx} className="hover:bg-slate-50">
                                                <td className="px-4 py-3">
                                                    <p className="font-medium">{row.supplier_name}</p>
                                                    <p className="text-xs text-slate-400">{row.supplier_id}</p>
                                                </td>
                                                <td className="px-4 py-3 text-center">{row.voucher_count}</td>
                                                <td className="px-4 py-3 text-center">{row.payment_count}</td>
                                                <td className="px-4 py-3 text-right font-mono">{fmt(row.total_gross_vouchered ?? row.total_vouchered)}</td>
                                                <td className="px-4 py-3 text-right font-mono text-rose-600">{fmt(row.total_withholding || 0)}</td>
                                                <td className="px-4 py-3 text-right font-mono">{fmt(row.total_vouchered)}</td>
                                                <td className="px-4 py-3 text-right font-mono text-emerald-600">{fmt(row.total_paid)}</td>
                                                <td className="px-4 py-3 text-right font-mono font-semibold text-amber-600">{fmt(row.outstanding)}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
